import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FxTable, isCurrency, type FxRate } from '@budget/core';
import { supabase } from '../../lib/supabase.js';
import { queryKeys } from '../../lib/query-client.js';

/**
 * `rate::text` is not decoration.
 *
 * PostgREST serialises `numeric` as a JSON number, so a plain `select('rate')`
 * hands JavaScript a double and the exactness the whole money layer is built
 * on is gone before the value ever reaches Decimal. The cast makes PostgREST
 * emit the decimal as text. Every query that reads an amount must do the same
 * — see apps/web/src/types/db.ts.
 */
const FX_SELECT = 'date, base, quote, rate::text';

/**
 * Pull the whole FX history once and keep it in memory.
 *
 * Four currencies over a few years is a handful of thousand rows — small
 * enough to hold client-side, which is exactly what lets a currency switch
 * re-render without a server round trip.
 */
export function useFxRates({ enabled }: { enabled: boolean }) {
  const query = useQuery({
    queryKey: queryKeys.fxRates,
    // Every table is default-deny, so asking before there is a session is a
    // guaranteed 401 and a console error on the login screen.
    enabled,
    // ECB publishes once a business day; re-asking more often is wasted work.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<FxRate[]> => {
      const { data, error } = await supabase
        .from('fx_rates_daily')
        .select(FX_SELECT)
        .order('date', { ascending: true });

      if (error) throw error;

      return (data ?? []).flatMap((row) => {
        // Defend against a currency arriving that v1 does not support.
        if (!isCurrency(row.base) || !isCurrency(row.quote)) return [];
        return [{ date: row.date, base: row.base, quote: row.quote, rate: row.rate }];
      });
    },
  });

  const fxTable = useMemo(() => {
    if (!query.data || query.data.length === 0) return null;
    return new FxTable(query.data);
  }, [query.data]);

  return { ...query, fxTable };
}
